/**
 * kc-warranty.js — LIVOM Premiumschutz (Helvetia) warranty upsell for the
 * CART DRAWER and the CART PAGE.
 *
 * It ports the PURE detection / bracket / body-pricing logic from the checkout
 * extension (livom-apps:
 *   apps/livom-checkout-upsell/extensions/checkout-warranty/src/warrantyLogic.js)
 * so both surfaces price the sofa identically, and enforces the SAME
 * single-warranty-line invariant by reconciling against /cart.js.
 *
 * SOURCE OF TRUTH = warrantyLogic.js. Do NOT diverge the bracket / detection /
 * `_part` body-sum rules from it. If the model changes, change it there first.
 *
 * Markets: DE / CH / AT only (this asset is only wired into those branches).
 *
 * The element is recreated whenever the drawer / cart page re-renders, so all
 * cross-instance state (the mutation lock) lives on `window`, and every cart
 * mutation re-reads the LIVE /cart.js before acting.
 */

(() => {
  'use strict';

  // ===========================================================================
  // PORTED PURE LOGIC (verbatim from warrantyLogic.js — keep in sync)
  // ===========================================================================

  /** Sofas priced above this (in store currency) have no bracket -> no upsell. */
  const MAX_INSURABLE_PRICE = 15000;

  /**
   * Product types that count as a stand-alone (non-configured) insurable sofa.
   * Keep in sync with SOFA_TYPES in warrantyLogic.js.
   *
   * Matching tolerates casing/whitespace (normalizeType) but NOT translation:
   * /cart.js returns `product_type` in the active storefront locale. `product_type`
   * is translated PER PRODUCT, so every new product of a type gets its own
   * auto-translation — deleting today's does not protect tomorrow's.
   *
   * Verified on livom.de (2026-07-27): "Sofa" has NO product_type translation (all
   * locales fall back to the German base), while "Schlafsofa" had 5. Those were
   * deleted but are listed here because Translate & Adapt re-creates them for new
   * products and the failure is silent (the offer simply never renders).
   */
  const SOFA_TYPES = [
    'Sofa',
    'Schlafsofa',
    'Sofa Bed', // en
    'Divano letto', // it
    'Canapé-lit', // fr
    'Sofá cama', // es
    'Slaapbank', // nl
  ];
  const CONFIGURATOR_TYPE = 'configurator';
  const FEET_TYPE = 'Füsse';

  /** Product types that are never part of the insured sofa and never an anchor. */
  const EXCLUDED_TYPES = new Set(['Cleaning products', 'Warranty']);

  /** Line item property the configurator stamps on every configured-set line. */
  const PART_ATTR = '_part';
  /** The only `_part` value that is insured. */
  const PART_BODY = 'body';

  // Legacy fallback signals (used ONLY when a group carries no `_part`).
  const CUSHION_DATA_ATTR = '_cushion_data';
  const FREE_COVER_SKU_PREFIX = 'FB-';

  function normalizeType(type) {
    return String(type == null ? '' : type).trim().toLowerCase();
  }

  const SOFA_TYPES_NORM = new Set(SOFA_TYPES.map(normalizeType));
  const CONFIGURATOR_TYPE_NORM = normalizeType(CONFIGURATOR_TYPE);
  const FEET_TYPE_NORM = normalizeType(FEET_TYPE);
  const EXCLUDED_TYPES_NORM = new Set([...EXCLUDED_TYPES].map(normalizeType));

  /** Price brackets. Ranges are half-open [min, maxExclusive). */
  const WARRANTY_BRACKETS = [
    { sku: 'WARRANTY-0000-0999', min: 0, maxExclusive: 1000 },
    { sku: 'WARRANTY-1000-1999', min: 1000, maxExclusive: 2000 },
    { sku: 'WARRANTY-2000-2999', min: 2000, maxExclusive: 3000 },
    { sku: 'WARRANTY-3000-4999', min: 3000, maxExclusive: 5000 },
    { sku: 'WARRANTY-5000-15000', min: 5000, maxExclusive: Infinity },
  ];

  /** The 5 bracket SKUs, used to recognize an existing warranty line in the cart. */
  const WARRANTY_BRACKET_SKUS = WARRANTY_BRACKETS.map((b) => b.sku);

  function round2(n) {
    return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
  }

  function getAttr(attributes, key) {
    if (!Array.isArray(attributes)) return undefined;
    const found = attributes.find((a) => a && a.key === key);
    return found ? found.value : undefined;
  }

  function pickBracketSku(price) {
    const p = round2(price);
    if (!Number.isFinite(p) || p < 0) return null;
    if (p > MAX_INSURABLE_PRICE) return null;
    const bracket = WARRANTY_BRACKETS.find((b) => p >= b.min && p < b.maxExclusive);
    return bracket ? bracket.sku : null;
  }

  function isLegacyBody(line) {
    const price = round2(line.lineTotal || 0);
    if (!(price > 0)) return false;
    if (normalizeType(line.productType) === FEET_TYPE_NORM) return false;
    if (String(line.sku || '').toUpperCase().startsWith(FREE_COVER_SKU_PREFIX)) return false;
    if (getAttr(line.attributes, CUSHION_DATA_ATTR) != null) return false;
    return true;
  }

  function stripConfigurationSuffix(title) {
    const cleaned = String(title || '')
      .replace(/[-\s_]*configuration\b/i, '')
      .trim();
    return cleaned || 'Sofa';
  }

  function configuredSofaName(groupLines, bodyLines) {
    const parent =
      groupLines.find((l) => getAttr(l.attributes, '_title')) ||
      groupLines.find((l) => getAttr(l.attributes, '_set')) ||
      groupLines.find((l) => /configuration/i.test(l.productTitle || ''));

    const titleProp = parent && getAttr(parent.attributes, '_title');
    if (titleProp) return titleProp;

    const source = parent || bodyLines[0] || groupLines[0];
    const raw = (source && (source.productTitle || source.variantTitle)) || 'Sofa';
    return stripConfigurationSuffix(raw);
  }

  function buildConfiguredUnit(setId, groupLines) {
    const groupHasPart = groupLines.some((l) => getAttr(l.attributes, PART_ATTR) != null);

    let bodyLines;
    let classificationPath;
    if (groupHasPart) {
      classificationPath = 'part-marker';
      bodyLines = groupLines.filter((l) => getAttr(l.attributes, PART_ATTR) === PART_BODY);
    } else {
      classificationPath = 'legacy-fallback';
      bodyLines = groupLines.filter((l) => isLegacyBody(l));
    }

    const price = round2(bodyLines.reduce((sum, l) => sum + Number(l.lineTotal || 0), 0));
    const currency =
      (bodyLines[0] && bodyLines[0].currency) || (groupLines[0] && groupLines[0].currency);

    return {
      kind: 'configured',
      setId,
      name: configuredSofaName(groupLines, bodyLines),
      price,
      currency,
      lineIds: groupLines.map((l) => l.id),
      classificationPath,
    };
  }

  function detectSofaUnits(lines) {
    const safeLines = Array.isArray(lines) ? lines : [];
    const units = [];

    // 1. Simple sofas: a SOFA_TYPES productType with no _set_id.
    for (const line of safeLines) {
      if (!SOFA_TYPES_NORM.has(normalizeType(line.productType))) continue;
      if (getAttr(line.attributes, '_set_id')) continue;
      units.push({
        kind: 'simple',
        setId: `simple-${line.variantNumericId || line.variantId || line.id}`,
        name: line.productTitle || line.variantTitle || 'Sofa',
        price: round2(line.lineTotal || 0),
        currency: line.currency,
        lineIds: [line.id],
        classificationPath: 'simple',
      });
    }

    // 2. Configured sofas: group "configurator" lines.
    const configuratorLines = safeLines.filter(
      (l) => normalizeType(l.productType) === CONFIGURATOR_TYPE_NORM
    );
    let usedFallback = false;

    if (configuratorLines.length > 0) {
      const withSetId = configuratorLines.filter((l) => getAttr(l.attributes, '_set_id'));

      if (withSetId.length > 0) {
        const groups = new Map();
        for (const line of withSetId) {
          const setId = getAttr(line.attributes, '_set_id');
          if (!groups.has(setId)) groups.set(setId, []);
          groups.get(setId).push(line);
        }
        for (const [setId, groupLines] of groups) {
          units.push(buildConfiguredUnit(setId, groupLines));
        }
      } else {
        usedFallback = true;
        units.push(buildConfiguredUnit('configurator-fallback', configuratorLines));
      }
    }

    return { units, usedFallback };
  }

  function computeWarrantyOffer(lines) {
    const candidateLines = (Array.isArray(lines) ? lines : []).filter(
      (l) => l && !EXCLUDED_TYPES_NORM.has(normalizeType(l.productType))
    );

    const { units, usedFallback } = detectSofaUnits(candidateLines);

    if (units.length === 0) {
      return { status: 'no-sofa', offer: null, usedFallback, classificationPath: null, units };
    }
    if (units.length > 1) {
      return { status: 'multiple-sofas', offer: null, usedFallback, classificationPath: null, units };
    }

    const unit = units[0];
    const price = round2(unit.price);
    const classificationPath = unit.classificationPath || null;

    if (!(price > 0)) {
      return { status: 'no-sofa', offer: null, usedFallback, classificationPath, units };
    }
    if (price > MAX_INSURABLE_PRICE) {
      return { status: 'over-max', offer: null, usedFallback, classificationPath, units };
    }

    const bracketSku = pickBracketSku(price);
    if (!bracketSku) {
      return { status: 'no-sofa', offer: null, usedFallback, classificationPath, units };
    }

    return {
      status: 'ok',
      offer: { setId: unit.setId, name: unit.name, price, currency: unit.currency, bracketSku },
      usedFallback,
      classificationPath,
      units,
    };
  }

  // ===========================================================================
  // THEME GLUE
  // ===========================================================================

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /** Single global lock: only one warranty cart mutation may be in flight. */
  function isLocked() {
    return window.__kcWarrantyLock === true;
  }
  function lock() {
    window.__kcWarrantyLock = true;
  }
  function unlock() {
    window.__kcWarrantyLock = false;
  }

  async function fetchCart() {
    const res = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`cart.js ${res.status}`);
    return res.json();
  }

  // ---------------------------------------------------------------------------
  // SPEED (Oct 1 2026): every change is ONE Cart AJAX API request (/cart/add.js
  // or /cart/update.js — Shopify's own, so the native product_added_to_cart
  // event and the tracking built on it keep firing). The page sections that
  // want to re-render (<main-cart> on /cart) are asked for in that same request
  // through `cart:prepare-bundled-sections` — the protocol <remove-set> already
  // uses — and the response is handed back with `mainCart:render`. No /cart.js
  // reads, no follow-up section fetch, no `cart:refresh` broadcast that made
  // every listener re-read the cart. Off /cart (nobody bundles) it falls back
  // to the old notify-and-refetch.
  // ---------------------------------------------------------------------------

  /** section ids the page wants back with the next cart mutation */
  function bundledSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    return [...new Set(sections)];
  }

  async function cartPost(url, body, withSections) {
    const sections = withSections ? bundledSections() : [];
    const payload = sections.length
      ? { ...body, sections, sections_url: window.location.pathname }
      : body;
    const res = await fetch(`${ROOT}${url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    let json = null;
    try {
      json = await res.json();
    } catch (e) {
      /* empty body */
    }
    if (!res.ok) throw new Error((json && (json.description || json.message)) || `${url} ${res.status}`);
    return { json, bundled: sections.length > 0 };
  }

  /** hand a bundled response to <main-cart> (one in-place re-render), or tell the page to refetch */
  function handOff(result) {
    if (result && result.bundled && result.json && result.json.sections) {
      document.dispatchEvent(new CustomEvent('mainCart:render', { detail: { cart: result.json } }));
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('mainCart:refresh'));
  }

  function warrantyProps(offer) {
    return {
      _insured_set_id: String(offer.setId),
      _insured_price: offer.price.toFixed(2),
      _insured_name: String(offer.name),
    };
  }

  /**
   * Make the cart's warranty lines what they should be, in as few requests as possible:
   *  - only remove  → ONE /cart/update.js with every warranty line at 0 (+ sections)
   *  - only add     → ONE /cart/add.js (+ sections)
   *  - replace      → /cart/update.js, then /cart/add.js (+ sections) — only when a stale or duplicate line exists
   */
  async function setWarranty(removeKeys, add) {
    const keys = (removeKeys || []).filter(Boolean);
    if (keys.length && !add) {
      const updates = Object.fromEntries(keys.map((k) => [k, 0]));
      return cartPost('cart/update.js', { updates }, true);
    }
    if (keys.length) {
      await cartPost('cart/update.js', { updates: Object.fromEntries(keys.map((k) => [k, 0])) }, false);
    }
    if (!add) return null;
    return cartPost(
      'cart/add.js',
      { items: [{ id: add.variant.variantId, quantity: 1, properties: warrantyProps(add.offer) }] },
      true
    );
  }

  /**
   * Map a /cart.js line into the detection-line shape the pure logic expects.
   * /cart.js exposes `product_type` directly (unlike checkout, which needed a
   * Storefront query), and `properties` as an OBJECT — converted to the
   * [{key, value}] array `getAttr` reads.
   */
  function buildDetectionLines(cart) {
    const currency = cart && cart.currency;
    return (cart && cart.items ? cart.items : []).map((line) => {
      const props = line.properties || {};
      const attributes = Object.keys(props).map((key) => ({
        key,
        value: props[key] == null ? '' : String(props[key]),
      }));
      return {
        id: line.key,
        quantity: line.quantity != null ? line.quantity : 1,
        lineTotal: (line.final_line_price != null ? line.final_line_price : line.line_price || 0) / 100,
        currency,
        productType: line.product_type || '',
        sku: line.sku || '',
        variantId: line.variant_id,
        variantNumericId: String(line.variant_id || ''),
        productTitle: line.product_title || line.title || '',
        variantTitle: line.variant_title || '',
        attributes,
      };
    });
  }

  /** Warranty lines currently in the cart, identified by stable bracket SKU. */
  function warrantyLinesFromCart(cart) {
    return (cart && cart.items ? cart.items : []).filter((i) =>
      WARRANTY_BRACKET_SKUS.includes(i.sku)
    );
  }

  // ===========================================================================
  // CUSTOM ELEMENT
  // ===========================================================================

  class KcWarranty extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this._data = this._parseData();
      this._catalog = this._buildCatalog(this._data);
      // 'card' = the offer row in the /cart list, 'summary' = the checkbox in the order summary, anything else = the
      // original drawer card. Card and checkbox live on the same page and drive the same line: only the card
      // reconciles (fixes a stale / duplicate warranty line), so the two never race each other for the cart.
      this._variant = this.dataset.variant || 'legacy';
      this._busy = false; // local UI-busy (button spinner)
      this._abort = new AbortController();

      // React to every theme cart event. `cart:change` carries the full cart (main-cart / theme) → no request at all;
      // the bare refresh events have no payload and fall back to one /cart.js read.
      const { signal } = this._abort;
      document.addEventListener('cart:change', (e) => this._scheduleRefresh(e && e.detail && e.detail.cart), { signal });
      document.addEventListener('cart:refresh', () => this._scheduleRefresh(), { signal });
      document.addEventListener('mainCart:refresh', () => this._scheduleRefresh(), { signal });

      // First paint from the cart the snippet rendered into the page (same server render as everything around it):
      // no request, no pop-in. Older markup without it reads /cart.js as before.
      this._refresh(this._data && this._data.cart && Array.isArray(this._data.cart.items) ? this._data.cart : null);
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      clearTimeout(this._refreshTimer);
    }

    // --- data ---------------------------------------------------------------

    _parseData() {
      const script = this.querySelector('.kc-warranty__data');
      if (!script) return { variants: [], strings: {}, config: {} };
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        console.error('[kc-warranty] bad data JSON', e);
        return { variants: [], strings: {}, config: {} };
      }
    }

    /** sku -> { variantId, premiumFormatted, available } from the Liquid catalog. */
    _buildCatalog(data) {
      const map = new Map();
      for (const v of data.variants || []) {
        if (v && WARRANTY_BRACKET_SKUS.includes(v.sku)) {
          map.set(v.sku, {
            variantId: v.id,
            premiumFormatted: v.priceFormatted || '',
            premiumCents: Number(v.price) || 0,
            compareFormatted: v.compareFormatted || '',
            available: !!v.available,
          });
        }
      }
      return map;
    }

    t(key) {
      const s = this._data && this._data.strings ? this._data.strings[key] : undefined;
      if (s == null) return '';
      // Liquid's `t` already HTML-escapes the translation ("Kinder &amp; Haustiere") and every render escapes again
      // with esc() — decode once here so the customer reads "&", not "&amp;".
      if (!/&[#a-z0-9]+;/i.test(s)) return s;
      const decoder = document.createElement('textarea');
      decoder.innerHTML = s;
      return decoder.value;
    }

    // --- refresh / reconcile -------------------------------------------------

    _scheduleRefresh(cart) {
      clearTimeout(this._refreshTimer);
      const usable = cart && Array.isArray(cart.items) ? cart : null;
      // a full cart in hand renders now; a bare "something changed" waits a beat so a burst costs one read
      if (usable) {
        this._refresh(usable);
        return;
      }
      this._refreshTimer = setTimeout(() => this._refresh(), 120);
    }

    async _refresh(givenCart) {
      // A mutation in flight will dispatch cart events when it settles; skip now.
      if (isLocked()) return;
      let cart = givenCart || null;
      if (!cart) {
        try {
          cart = await fetchCart();
        } catch (e) {
          console.error('[kc-warranty] cart fetch failed', e);
          return;
        }
      }
      this._cart = cart;

      const result = computeWarrantyOffer(buildDetectionLines(cart));
      const warrantyLines = warrantyLinesFromCart(cart);

      // Reconcile FIRST (enforce the single-line invariant + orphan removal).
      const mutated = this._variant === 'summary' ? false : await this._reconcile(result, warrantyLines);
      if (mutated) {
        // The mutation already notified the host UIs, which re-render us.
        return;
      }

      this._render(result, warrantyLines, cart);
    }

    /**
     * Enforce the invariant without ever auto-adding:
     *  - 0 warranty lines        -> leave alone (consent required to add).
     *  - exactly 1, correct sku, variant available, offer ok -> leave alone.
     *  - anything else           -> remove all warranty lines, then re-add the
     *    correct bracket ONLY when the cart still maps to a single insurable
     *    sofa (status 'ok' + variant available). This auto-removes the warranty
     *    when the sofa is removed / becomes uninsurable (none, >15000, 2+ sofas)
     *    or when it must move brackets.
     * @returns {Promise<boolean>} whether the cart was mutated.
     */
    async _reconcile(result, warrantyLines) {
      if (!warrantyLines || warrantyLines.length === 0) return false;
      if (isLocked()) return false;

      const desiredSku = result.status === 'ok' && result.offer ? result.offer.bracketSku : null;
      const desiredVariant = desiredSku ? this._catalog.get(desiredSku) : null;
      const desiredOk = !!(desiredVariant && desiredVariant.available);

      const alreadyCorrect =
        warrantyLines.length === 1 && desiredOk && warrantyLines[0].sku === desiredSku;
      if (alreadyCorrect) return false;

      lock();
      let response = null;
      try {
        response = await setWarranty(
          warrantyLines.map((l) => l.key),
          desiredOk && result.offer ? { offer: result.offer, variant: desiredVariant } : null
        );
      } catch (e) {
        console.error('[kc-warranty] reconcile failed', e);
      } finally {
        unlock();
      }
      handOff(response);
      return true;
    }

    // --- user toggle ---------------------------------------------------------

    /**
     * Say "working on it" until the page re-renders (a second or two): the summary row gets the same spinner and
     * "Wird hinzugefügt …" / "Wird entfernt …" as the old-sofa pickup (assets/cart.css .kc-cart-pickup.is-busy); the
     * card keeps its place and its button turns into a spinner with the same words — nothing jumps or disappears
     * before the warranty line is actually there.
     */
    _showBusy(wantOn) {
      const row = this.querySelector('.kc-cart-pickup');
      if (row) row.classList.add('is-busy', wantOn ? 'is-adding' : 'is-removing');
      // the card: the page's one loader (assets/cart.css .kc-loading) — the same ring as every other change on /cart
      this.querySelector('.kc-warranty-offer')?.classList.add('kc-loading');
    }

    _clearBusy() {
      const row = this.querySelector('.kc-cart-pickup');
      if (row) row.classList.remove('is-busy', 'is-adding', 'is-removing');
      this.querySelector('.kc-warranty-offer')?.classList.remove('kc-loading');
    }

    /**
     * The customer ticked / clicked: the row or button shows it is working (_showBusy), the cart follows with ONE
     * request, and the page re-renders from that same response. On failure the previous state comes back with the
     * error line.
     */
    async _applyWarranty(wantOn) {
      if (isLocked() || this._busy) return;
      this._busy = true;
      this._setError(false);
      this._showBusy(wantOn);

      lock();
      let response = null;
      let failed = false;
      try {
        const cart = this._cart || (await fetchCart());
        const result = computeWarrantyOffer(buildDetectionLines(cart));
        const lines = warrantyLinesFromCart(cart);

        if (wantOn) {
          const desiredSku = result.status === 'ok' && result.offer ? result.offer.bracketSku : null;
          const desiredVariant = desiredSku ? this._catalog.get(desiredSku) : null;
          if (!desiredVariant || !desiredVariant.available || !result.offer) {
            failed = true; // sofa no longer insurable
          } else {
            // a stale / duplicate line goes in the same go; normally there is none and this is ONE add
            response = await setWarranty(
              lines.map((l) => l.key),
              { offer: result.offer, variant: desiredVariant }
            );
          }
        } else if (lines.length) {
          response = await setWarranty(lines.map((l) => l.key), null);
        }
      } catch (e) {
        console.error('[kc-warranty] toggle failed', e);
        failed = true;
      } finally {
        unlock();
        this._busy = false;
      }

      if (failed) {
        this._clearBusy();
        if (this._cart) this._refresh(this._cart); // back to what the cart really holds
        this._setError(true);
        return;
      }
      handOff(response);
    }

    // --- rendering -----------------------------------------------------------

    _ensureMount() {
      if (!this._mount) {
        this._mount = document.createElement('div');
        this._mount.className = 'kc-warranty__mount';
        this.appendChild(this._mount);
      }
      return this._mount;
    }

    _hide() {
      this.hidden = true;
      if (this._mount) this._mount.innerHTML = '';
    }

    _show() {
      this.hidden = false;
    }

    _setError(on) {
      this._error = on;
      const banner = this.querySelector('.kc-warranty__error');
      if (banner) banner.hidden = !on;
    }

    _setButtonBusy(on) {
      const btn = this.querySelector('.kc-warranty__action');
      if (btn) {
        btn.disabled = on;
        btn.classList.toggle('is-busy', on);
      }
    }

    _render(result, warrantyLines, cart) {
      const status = result.status;

      if (this._variant === 'summary' || this._variant === 'card') {
        const sku = status === 'ok' && result.offer ? result.offer.bracketSku : null;
        const variant = sku ? this._catalog.get(sku) : null;
        if (!variant || !variant.available) {
          // The summary is a price list: notices (2+ sofas, over 15.000) belong to the card, never to the summary.
          if (this._variant === 'card' && status === 'multiple-sofas') {
            this._renderNotice(this.t('multipleSofasHeadline'), this.t('multipleSofasNotice'));
          } else if (this._variant === 'card' && status === 'over-max') {
            this._renderOverMax();
          } else {
            this._hide();
          }
          return;
        }
        const isChecked = warrantyLines.length > 0 && warrantyLines[0].sku === sku;
        if (this._variant === 'summary') this._renderSummary(variant, isChecked);
        // Once the warranty is in the cart (from this card, the summary checkbox or straight from the configurator)
        // the offer is done: the card goes away and the warranty shows as an ordinary line of the order, like any
        // other add-on (Ivan, Oct 1 2026).
        else if (isChecked) this._hide();
        else this._renderCard(variant, false);
        return;
      }

      if (status === 'multiple-sofas') {
        this._renderNotice(this.t('multipleSofasHeadline'), this.t('multipleSofasNotice'));
        return;
      }

      if (status === 'over-max') {
        this._renderOverMax();
        return;
      }

      if (status !== 'ok' || !result.offer) {
        this._hide();
        return;
      }

      const desiredSku = result.offer.bracketSku;
      const variant = this._catalog.get(desiredSku);
      if (!variant || !variant.available) {
        // Bracket variant not configured / out of stock for this store.
        console.warn(
          `[kc-warranty] no available warranty variant for ${desiredSku}; check the warranty product setting and that all WARRANTY-* SKUs exist and are in stock.`
        );
        this._hide();
        return;
      }

      const isChecked =
        warrantyLines.length > 0 && warrantyLines[0].sku === desiredSku;

      const sofaImg = this._sofaImageUrl(cart, result.units && result.units[0]);
      this._renderOffer(variant, isChecked, sofaImg);
    }

    /**
     * Thumbnail of the sofa being insured: the image of the highest-priced line
     * within the detected sofa unit (so a configured set shows the sofa body,
     * not an accessory). Returns '' when no line carries an image.
     */
    _sofaImageUrl(cart, unit) {
      // Only simple sofas have a usable product image. Configured sofas (built
      // in the configurator) would otherwise show a configuration piece's image,
      // so we show none — matching the checkout extension.
      if (!cart || !unit || unit.kind !== 'simple' || !Array.isArray(unit.lineIds)) return '';
      const ids = new Set(unit.lineIds);
      let best = null;
      for (const item of cart.items || []) {
        if (!ids.has(item.key)) continue;
        const url = (item.featured_image && item.featured_image.url) || item.image;
        if (!url) continue;
        const cost = item.final_line_price || 0;
        if (!best || cost > best.cost) best = { url, cost };
      }
      if (!best) return '';
      // Ask the Shopify CDN for a small render instead of the full image.
      return best.url + (best.url.indexOf('?') === -1 ? '?' : '&') + 'width=400';
    }

    _renderNotice(headline, body) {
      this._show();
      const mount = this._ensureMount();
      mount.innerHTML = `
        <div class="kc-warranty__card kc-warranty__card--notice" role="note">
          <p class="kc-warranty__notice-title">${esc(headline)}</p>
          <p class="kc-warranty__notice-text">${esc(body)}</p>
        </div>`;
    }

    _renderOverMax() {
      this._show();
      const mount = this._ensureMount();
      const serviceUrl = (this._data.config && this._data.config.serviceUrl) || '';
      const cta = serviceUrl
        ? `<a class="button kc-warranty__cta" href="${esc(serviceUrl)}" target="_blank" rel="noopener">${esc(this.t('highValueCta'))}</a>`
        : '';
      mount.innerHTML = `
        <div class="kc-warranty__card kc-warranty__card--notice">
          <p class="kc-warranty__title">${esc(this.t('overline'))}</p>
          <p class="kc-warranty__subtitle">${esc(this.t('highValueHeadline'))}</p>
          <p class="kc-warranty__notice-text">${esc(this.t('highValueText'))}</p>
          ${cta}
        </div>`;
    }

    _renderOffer(variant, isChecked, sofaImg) {
      this._show();
      const mount = this._ensureMount();

      const priceLine = String(this.t('priceLine')).replace(
        '[price]',
        variant.premiumFormatted || ''
      );
      const benefits = [this.t('benefit1'), this.t('benefit2'), this.t('benefit3')]
        .filter(Boolean)
        .map((b) => `<li class="kc-warranty__benefit">${ICONS.checkCircle}<span>${esc(b)}</span></li>`)
        .join('');

      const terms = this._termsLinks()
        .map(
          (l) =>
            `<a class="kc-warranty__terms-link" href="${esc(l.href)}" target="_blank" rel="noopener">${esc(l.label)}</a>`
        )
        .join('');

      const action = isChecked
        ? `<div class="kc-warranty__added">
             <span class="kc-warranty__added-label">${ICONS.check}${esc(this.t('added'))}</span>
             <button type="button" class="kc-warranty__action kc-warranty__remove-link" data-want="off">${esc(this.t('remove'))}</button>
           </div>`
        : `<button type="button" class="button kc-warranty__action kc-warranty__action--add" data-want="on">${esc(this.t('accept'))}</button>`;

      const intro = `
        <div class="kc-warranty__intro">
          <p class="kc-warranty__title">${esc(this.t('overline'))}</p>
          <p class="kc-warranty__subtitle">${esc(this.t('headline'))}</p>
          <p class="kc-warranty__price">${esc(priceLine)}</p>
        </div>`;
      const top = sofaImg
        ? `<div class="kc-warranty__top">
             ${intro}
             <figure class="kc-warranty__figure">
               <img class="kc-warranty__img" src="${esc(sofaImg)}" alt="${esc(this.t('headline'))}" loading="lazy" />
             </figure>
           </div>`
        : intro;

      mount.innerHTML = `
        <div class="kc-warranty__card${isChecked ? ' is-checked' : ''}">
          ${top}
          <ul class="kc-warranty__benefits">${benefits}</ul>
          <p class="kc-warranty__error" hidden>${esc(this.t('errorAdding'))}</p>
          <div class="kc-warranty__actions">${action}</div>
          ${terms ? `<div class="kc-warranty__terms">${terms}</div>` : ''}
        </div>`;

      const btn = mount.querySelector('.kc-warranty__action');
      if (btn) {
        btn.addEventListener('click', () => {
          this._applyWarranty(btn.getAttribute('data-want') === 'on');
        });
      }
      if (this._error) this._setError(true);
    }

    /** price + struck-through reference (premium + 200, from Liquid's money filter so it reads like every other price) */
    _priceHtml(variant) {
      const compare = variant.compareFormatted
        ? `<s class="kc-warranty-offer__compare">${esc(variant.compareFormatted)}</s>`
        : '';
      return `<span class="kc-warranty-offer__amount">${esc(variant.premiumFormatted || '')}</span>${compare}`;
    }

    /**
     * /cart list: the same row card as the second cover (assets/cart.css .kc-cart-offer--row), always AFTER it (Ivan,
     * Oct 1 2026). The product photo, the title, the price with its reference, what it covers one per line, one
     * button. Once added the card stays, turned into a confirmation: the protection is part of the order.
     */
    _renderCard(variant, isChecked) {
      this._show();
      const mount = this._ensureMount();
      const image = (this._data.config && this._data.config.image) || '';
      const media = image
        ? `<img class="kc-cart-offer__image kc-warranty-offer__photo" src="${esc(image)}" alt="" loading="lazy" decoding="async" width="120" height="120">`
        : ICONS.shield;
      const benefits = [this.t('short1'), this.t('short2'), this.t('short3')]
        .filter(Boolean)
        .map((b) => `<li class="kc-warranty-offer__benefit">${ICONS.tick}<span>${esc(b)}</span></li>`)
        .join('');
      const terms = this._termsLinks()
        .map((l) => `<a class="kc-warranty-offer__terms-link" href="${esc(l.href)}" target="_blank" rel="noopener">${esc(l.label)}</a>`)
        .join('<span aria-hidden="true"> · </span>');

      const bar = isChecked
        ? `<div class="kc-cart-line__bar kc-cart-offer__bar kc-warranty-offer__bar">
             <div class="kc-warranty-offer__state">
               <span class="kc-warranty-offer__done">${ICONS.check}${esc(this.t('addedLong'))}</span>
               <button type="button" class="kc-warranty-offer__remove kc-warranty__action" data-want="off">${esc(this.t('remove'))}</button>
             </div>
           </div>`
        : `<div class="kc-cart-line__bar kc-cart-offer__bar kc-warranty-offer__bar">
             <button type="button" class="kc-cart-line__bar-action kc-cart-offer__add kc-warranty__action" data-want="on">
               <svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>${esc(this.t('accept'))}
             </button>
           </div>`;

      mount.innerHTML = `
        <article class="kc-cart-offer kc-cart-offer--row kc-warranty-offer${isChecked ? ' is-checked' : ''}">
          ${this.t('badge') ? `<span class="kc-warranty-offer__badge">${esc(this.t('badge'))}</span>` : ''}
          <span class="kc-cart-line__media kc-cart-offer__media kc-warranty-offer__media${image ? ' has-photo' : ''}" aria-hidden="true">${media}</span>
          <div class="kc-cart-line__info kc-cart-offer__body">
            <div class="kc-cart-offer__head">
              <span class="kc-cart-offer__title">${esc(this.t('cardTitle'))}</span>
            </div>
            <p class="kc-warranty-offer__price">${this._priceHtml(variant)}</p>
            <ul class="kc-warranty-offer__benefits">${benefits}</ul>
            ${terms ? `<p class="kc-warranty-offer__terms">${terms}</p>` : ''}
            <p class="kc-warranty__error" hidden>${esc(this.t('errorAdding'))}</p>
          </div>
          ${bar}
        </article>`;

      const btn = mount.querySelector('.kc-warranty__action');
      if (btn) btn.addEventListener('click', () => this._applyWarranty(btn.getAttribute('data-want') === 'on'));
      if (this._error) this._setError(true);
    }

    /**
     * Order summary: one flat checkbox row, the twin of "Altes Sofa mitnehmen lassen" (blocks/_kc-cart-form-pickup)
     * — same classes, so the two read as one list of optional services. Never pre-ticked: it is an insurance, the
     * customer has to choose it.
     */
    _renderSummary(variant, isChecked) {
      this._show();
      const mount = this._ensureMount();
      mount.innerHTML = `
        <label class="kc-cart-pickup kc-warranty-summary">
          <input type="checkbox" class="kc-cart-pickup__input kc-warranty__action" ${isChecked ? 'checked' : ''}>
          <span class="kc-cart-pickup__box" aria-hidden="true">
            <svg viewBox="0 0 12 12" fill="none"><path d="m2.5 6.2 2.3 2.3 4.7-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </span>
          <span class="kc-cart-pickup__text">
            <span class="kc-cart-pickup__title">${esc(this.t('summaryTitle'))}</span>
            <span class="kc-cart-pickup__note">${esc(this.t('summaryNote'))}</span>
          </span>
          <span class="kc-cart-pickup__price kc-warranty-summary__price" data-adding="${esc(this.t('adding'))}" data-removing="${esc(this.t('removing'))}">
            ${variant.compareFormatted ? `<s class="kc-warranty-summary__compare">${esc(variant.compareFormatted)}</s>` : ''}
            <span>+${esc(variant.premiumFormatted || '')}</span>
          </span>
        </label>
        <p class="kc-warranty__error" hidden>${esc(this.t('errorAdding'))}</p>`;
      const input = mount.querySelector('input');
      input.addEventListener('change', () => this._applyWarranty(input.checked));
      if (this._error) this._setError(true);
    }

    _termsLinks() {
      const cfg = this._data.config || {};
      const links = [];
      if (cfg.avbUrl) links.push({ href: cfg.avbUrl, label: this.t('termsAvb') });
      if (cfg.ipidUrl) links.push({ href: cfg.ipidUrl, label: this.t('termsIpid') });
      if (links.length === 0 && cfg.termsUrl) {
        links.push({ href: cfg.termsUrl, label: this.t('terms') });
      }
      return links;
    }
  }

  // --- small helpers ---------------------------------------------------------

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  const ICONS = {
    check:
      '<svg class="kc-warranty__icon kc-warranty__icon--check" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="10" fill="currentColor"/><path d="M7 12.5l3.2 3.2L17 9" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    tick:
      '<svg class="kc-warranty-offer__tick" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3.5 8.4l2.8 2.8 6.2-6.4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    shield:
      '<svg class="kc-warranty-offer__shield" viewBox="0 0 48 48" aria-hidden="true" focusable="false"><path d="M24 5l15 5.5v11.2c0 9.6-6.3 17.6-15 20.3-8.7-2.7-15-10.7-15-20.3V10.5L24 5z" fill="currentColor" opacity=".16"/><path d="M24 5l15 5.5v11.2c0 9.6-6.3 17.6-15 20.3-8.7-2.7-15-10.7-15-20.3V10.5L24 5z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M17 24.2l5 5 9-9.6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    checkCircle:
      '<svg class="kc-warranty__bicon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M8 12.4l2.6 2.6L16 9.4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  };

  if (!window.customElements.get('kc-warranty')) {
    window.customElements.define('kc-warranty', KcWarranty);
  }
})();
